import { ReservationShift } from 'types/shifts';

type Additional = NonNullable<ReservationShift['additional']>;
export type DayOverrideValues = Partial<
  Pick<Additional, 'slotsPerBlock' | 'eventV02' | 'countAllGuests'>
> & { maxGuests?: number | null; blockReservations?: boolean };
export const DAY_OVERRIDE_FIELDS = [
  'maxGuests',
  'slotsPerBlock',
  'eventV02',
  'countAllGuests',
  'blockReservations',
] as const;
export type DayOverrideField = typeof DAY_OVERRIDE_FIELDS[number];
export type DayOverrideChanges = { set: DayOverrideValues; reset: DayOverrideField[] };
type Shift = Partial<ReservationShift>;

export const hasDayOverride = (shift: Shift, field: DayOverrideField) =>
  Object.prototype.hasOwnProperty.call(shift.overwrite ?? {}, field);

export const effectiveShiftSettings = (shift: Shift, series = false): Additional =>
  ({ ...shift.additional, ...(series ? {} : shift.overwrite) } as Additional);

export function applyDayOverrideChanges<T extends Shift>(
  shift: T,
  changes: DayOverrideChanges
): T {
  const overwrite = { ...shift.overwrite, ...changes.set } as DayOverrideValues;
  let additional = shift.additional;
  for (const field of changes.reset) {
    if (field === 'eventV02' && additional?.eventV02) {
      const currentEvent = effectiveShiftSettings(shift).eventV02;
      if (currentEvent?.id) {
        additional = {
          ...additional,
          eventV02: { ...additional.eventV02, id: currentEvent.id },
        };
      }
    }
    delete overwrite[field];
  }
  return { ...shift, ...(additional === undefined ? {} : { additional }), overwrite };
}

export function dayOverrideChanges(before: Shift, after: Shift): DayOverrideChanges {
  const set: DayOverrideValues = {};
  const reset: DayOverrideField[] = [];
  const beforeOverrides = before.overwrite as DayOverrideValues | undefined;
  const afterOverrides = after.overwrite as DayOverrideValues | undefined;
  for (const field of DAY_OVERRIDE_FIELDS) {
    if (
      JSON.stringify(beforeOverrides?.[field]) === JSON.stringify(afterOverrides?.[field])
    )
      continue;
    if (hasDayOverride(after, field))
      Object.assign(set, { [field]: afterOverrides?.[field] });
    else reset.push(field);
  }
  return { set, reset };
}

export function changeShiftSettings<T extends Shift>(
  shift: T,
  values: Partial<Additional>,
  dayOnly: boolean
): T {
  if (!dayOnly) return { ...shift, additional: { ...shift.additional, ...values } };
  const effective = effectiveShiftSettings(shift);
  let additional = shift.additional;
  const changes: DayOverrideChanges = { set: {}, reset: [] };
  for (const field of Object.keys(values) as (keyof Additional)[]) {
    if (JSON.stringify(effective[field]) === JSON.stringify(values[field])) continue;
    if (!DAY_OVERRIDE_FIELDS.includes(field as DayOverrideField)) {
      additional = { ...additional, [field]: values[field] } as Additional;
      continue;
    }
    if (field === 'maxGuests' && values[field] === undefined) changes.set.maxGuests = null;
    else if (values[field] === undefined) changes.reset.push(field as DayOverrideField);
    else Object.assign(changes.set, { [field]: values[field] });
  }
  return applyDayOverrideChanges(
    additional === undefined ? shift : { ...shift, additional },
    changes
  );
}

export const hasCapacityOverride = (shift: Shift) =>
  (['maxGuests', 'slotsPerBlock', 'eventV02'] as const).some((field) =>
    hasDayOverride(shift, field)
  );
